/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 导出分析结果按钮组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-07-31 14:57:40
 */
import React, { useCallback } from 'react'
import { App } from 'antd'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { updateAIRecognitionResult } from '../../../actions/aiRecognition'
import { XCRecognitionExportDataResult } from '../types'
import { useAppDispatch } from '../../../app/hooks'
import { useSensorTracking } from '../hooks/useSensorTracking'
import { usePollAndDownload } from '../hooks/usePollAndDownload'
import { FileWordTwoTone } from '@ant-design/icons'

/**
 * 导出分析结果按钮组件接口
 */
export interface XCExportResultButtonProps {
  className?: string
  style?: React.CSSProperties
}

/**
 * 导出分析结果按钮组件
 * 封装导出功能的完整逻辑，包括按钮UI和导出处理
 */
export const ExportResultButton: React.FC<XCExportResultButtonProps> = ({
  className = 'export-button',
  style
}) => {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const { reportAISafetyHazardsClick } = useSensorTracking()
  const { pollAndDownload } = usePollAndDownload()
  const { recognitionResult, userId, groupId } = useAIRecognition()

  /**
   * 处理导出按钮点击
   * 调用导出接口并下载文件
   */
  const handleExport = useCallback(async () => {
    if (!recognitionResult || !userId || !groupId) {
      message.error('缺少必要的数据，无法导出')
      return
    }

    try {
      const result = await dispatch(
        updateAIRecognitionResult({
          groupID: groupId,
          id: recognitionResult.id,
          potentialHazardList: recognitionResult.potentialHazardList,
          standardList: recognitionResult.standardList,
          userID: userId
        })
      )

      const response = result as XCRecognitionExportDataResult

      if (response.exportURL && response.exportURL.length) {
        message.success('正在生成文件，请稍候...')

        // 下载Word文档
        pollAndDownload({
          exportURL: response.exportURL,
          fileName: String(response.fileName),
          defaultFileName: 'recognition_report.docx'
        })
          .then(() => {
            message.success('文件下载成功')
          })
          .catch((error) => {
            console.error('下载失败:', error)
            message.error('下载失败，请稍后重试')
          })
      } else {
        throw new Error('导出失败')
      }
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败，请重试')
    }
  }, [recognitionResult, userId, groupId, pollAndDownload, dispatch])

  /**
   * 处理按钮点击事件
   * 包含埋点上报和导出逻辑
   */
  const handleClick = useCallback(() => {
    reportAISafetyHazardsClick({ clickItem: 'exportWord' })
    handleExport()
  }, [reportAISafetyHazardsClick, handleExport])

  return (
    <button className={className} style={style} onClick={handleClick}>
      <span>导出分析结果</span>
      {/* Word图标 */}
      <FileWordTwoTone />
    </button>
  )
}

export default ExportResultButton
